var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { RemoveSubModelMessage, InputMappingFacet, MovementInputMappingFacet, RoomChangedMessage, ROOT_MODEL_UID, LayoutUpdatedMessage, PendingMessages, ButtonSignal, ControllerButtonMessage, ButtonType, MulticastDelegate } from "flavr-common";
import { Vector2, Vector3 } from "flavr-math";
import { busService } from "../../bus";
import { GamepadsDevice } from "./GamepadsDevice";
import { renderer } from "../../rendering/RendererGlobals";
import { inputKeyMappingService } from "./InputKeyMappingService";
import { InputActionKeyBindingsMessage, InputActionMessage, InputActionTriggeredMessage, InputAxisValueMessage, InputFlaVRDefaultActionMessage, InputFlaVRDefaultActionType, RegisterInputActionMessage, ResponseRegisterInputActionMessage, UnregisterInputActionMessage } from "../../../messages/input/InputMessages";
import { DEFAULT_KEY_ACTION_MAPPING } from "./DefaultMapping";
import { InputKeySpecial } from "./InputTypes";
import { InputKeyDetailsDeviceEnum } from "./InputKeyDetails";
import { preferencesService } from "../../preferences/PreferencesService";
import { modelPool } from "@modules/model";
const DOUBLE_CLICK_TIME = 500;
const HOLD_TIME = 1000;
const KEY_MAP = new Map();
class InputKeyData {
    constructor(keyName, action) {
        this.keyName = keyName;
        this.action = action;
        this.shift = false;
        this.ctrl = false;
        this.alt = false;
        this.cmd = false;
        this.isForDev = false;
    }
    addSpecial(special) {
        switch (special) {
            case InputKeySpecial.Shift:
                this.shift = true;
                break;
            case InputKeySpecial.Ctrl:
                this.ctrl = true;
                break;
            case InputKeySpecial.Alt:
                this.alt = true;
                break;
            case InputKeySpecial.Cmd:
                this.cmd = true;
                break;
        }
        inputService.onActionsChangedDelegate.invoke();
        return this;
    }
    asDev() {
        this.shift = true;
        this.isForDev = true;
        inputService.onActionsChangedDelegate.invoke();
    }
    canTrigger(specialKeysState) {
        if (!this.action.enabled)
            return false;
        if (specialKeysState.shift !== this.shift)
            return false;
        if (specialKeysState.ctrl !== this.ctrl)
            return false;
        if (specialKeysState.alt !== this.alt)
            return false;
        return specialKeysState.cmd === this.cmd;
    }
}
class InputAction {
    constructor(name, callback, description) {
        this.name = name;
        this.callback = callback;
        this.description = description;
        this.disabledCount = 0;
        this.keys = new Map();
    }
    get enabled() {
        return this.disabledCount == 0;
    }
    set enabled(value) {
        if (value) {
            if (this.disabledCount === 0)
                return;
            --this.disabledCount;
        }
        else {
            this.disabledCount++;
        }
    }
    addKey(keyName) {
        const keyDetails = inputKeyMappingService.getKeyDetails(keyName);
        if (!keyDetails) {
            throw new Error(`Can't find key details for key name ${keyName}`);
        }
        let actions = KEY_MAP.get(keyName);
        /* if (actions != undefined) {
          throw new Error(`key "${key}" already added in action ${action.name}`);
        } */
        /* const keyboardMapping = BUTTON_MAPPING[InputDeviceType.Keyboard];
        if (keyboardMapping[key] != undefined) {
          throw new Error(`key "${key}" already added in BUTTON_MAPPING`);
        } */
        const keyData = new InputKeyData(keyName, this);
        this.keys.set(keyName, keyData);
        if (!actions) {
            actions = [];
            if (keyDetails.deviceType.type === InputKeyDetailsDeviceEnum.Keyboard) {
                const key = keyDetails.deviceType.key || keyDetails.deviceType.code;
                if (key) {
                    KEY_MAP.set(key, actions);
                }
            }
        }
        actions.push(keyData);
        inputService.onActionsChangedDelegate.invoke();
        return keyData;
    }
    *getKeyIterator() {
        for (const key of this.keys) {
            yield key;
        }
    }
    // ToDo: support mouse and gamepad
    clear() {
        for (const data of this.keys) {
            for (const [key, mapKeyData] of KEY_MAP) {
                if (mapKeyData != data)
                    continue;
                KEY_MAP.delete(key);
            }
        }
        this.keys.clear();
        inputService.onActionsChangedDelegate.invoke();
    }
    hasDevKey() {
        for (const data of this.keys.values()) {
            if (data.isForDev)
                return true;
        }
        return false;
    }
    _fromWorker(workerId, actionIndex) {
        this.worker = {
            workerId,
            actionIndex
        };
    }
}
var KeyState;
(function (KeyState) {
    KeyState[KeyState["Released"] = 0] = "Released";
    KeyState[KeyState["Pressed"] = 1] = "Pressed";
    KeyState[KeyState["Hold"] = 2] = "Hold";
})(KeyState || (KeyState = {}));
class InputService {
    constructor() {
        this.actions = new Map();
        this.workerActions = new Map();
        this.codesDown = new Map();
        this.keysDown = new Map();
        this.specialKeysState = {
            shift: false,
            ctrl: false,
            alt: false,
            cmd: false
        };
        this.mouseButtons = new Map();
        this._mouseScrollDelta = new Vector3();
        this.lastDisabledId = 0;
        this.disabled = new Set();
        this.mousePos = new Vector2();
        this.mouseMovement = new Vector2();
        this.viewHalfX = 0;
        this.viewHalfY = 0;
        this.connection = busService.connection("InputService");
        this.currentRoomModelUID = ROOT_MODEL_UID;
        this.roomInputMapping = new Map();
        this.defaultAxisMapping = new Map();
        this.currentAxisMapping = new Map();
        this.currentAxisConfigs = new Map();
        this.currentActionInfos = new Map();
        this.currentKeyActionsBinding = new Map();
        this.defaultKeyActionsBinding = new Map();
        this.defaultKeyActionInfos = new Map();
        this.mouseWheelSensitivity = 0.5;
        this.userMouseSensitivity = 1;
        this.gamepadsDevice = new GamepadsDevice();
        this.needsUpdate = false;
        this.pendingMessages = new PendingMessages();
        this.onActionsChangedDelegate = new MulticastDelegate();
        this.nextActionIndex = 0;
        this.canvasResizeObserver = null;
        this.onKeyDown = (event) => __awaiter(this, void 0, void 0, function* () {
            this.specialKeysState.alt = event.altKey;
            this.specialKeysState.ctrl = event.ctrlKey;
            this.specialKeysState.shift = event.shiftKey;
            this.specialKeysState.cmd = event.metaKey;
            if (this.isDisabled())
                return;
            if (document.activeElement) {
                if (document.activeElement.contentEditable === true) {
                    return;
                }
                const nodeName = document.activeElement.nodeName;
                if (["TEXTAREA", "INPUT"].includes(nodeName)) {
                    return;
                }
            }
            const time = Date.now();
            // Lower case A-Z
            const keyLowerCase = event.key.length === 1 ? event.key.toLowerCase() : event.key;
            const keyFromLayout = yield this.getKeyFromLayoutMap(event.code);
            this.setKeyDown(this.codesDown, event.code, time);
            this.setKeyDown(keyFromLayout || this.keysDown, keyLowerCase, time);
            this.setKeyDown(this.keysDown, keyLowerCase, time);
            this.triggerKeyAction(ButtonSignal.PRESSED, event.code, keyLowerCase);
        });
        this.onKeyUp = (event) => __awaiter(this, void 0, void 0, function* () {
            const time = Date.now();
            this.specialKeysState.alt = event.altKey;
            this.specialKeysState.ctrl = event.ctrlKey;
            this.specialKeysState.shift = event.shiftKey;
            this.specialKeysState.cmd = event.metaKey;
            // Lower case A-Z
            const keyLowerCase = event.key && event.key.length === 1 ? event.key.toLowerCase() : event.key;
            const keyFromLayout = yield this.getKeyFromLayoutMap(event.code);
            const codeData = this.onPreKeyUp(this.codesDown, event.code, time);
            const keyData = this.onPreKeyUp(keyFromLayout || this.keysDown, keyLowerCase, time);
            if (this.isDisabled())
                return;
            if (codeData.state === KeyState.Pressed) {
                if (time - codeData.lastChanged > DOUBLE_CLICK_TIME) {
                    this.triggerKeyAction(ButtonSignal.HOLD, event.code, keyLowerCase);
                }
                else {
                    this.triggerKeyAction(codeData.doubleClick ? ButtonSignal.DOUBLE_CLICK : ButtonSignal.CLICKED, event.code, keyLowerCase);
                    codeData.doubleClick = !codeData.doubleClick;
                }
            }
            if (codeData.state === KeyState.Hold) {
                this.triggerKeyAction(ButtonSignal.HOLD_RELEASED, event.code, keyLowerCase);
            }
            this.triggerKeyAction(ButtonSignal.RELEASED, event.code, keyLowerCase);
            codeData.state = KeyState.Released;
            codeData.lastChanged = time;
            keyData.state = KeyState.Released;
            keyData.lastChanged = time;
            const keys = KEY_MAP.get(event.code) || KEY_MAP.get(keyLowerCase);
            if (keys === undefined)
                return;
            for (const data of keys) {
                if (!data.canTrigger(this.specialKeysState))
                    continue;
                data.action.callback();
            }
        });
        this.onMouseMove = (event) => {
            if (this.isDisabled())
                return;
            this.mousePos.set(event.pageX - this.viewHalfX, event.pageY - this.viewHalfY);
            const movementX = event.movementX || event.mozMovementX || event.webkitMovementX || 0;
            const movementY = event.movementY || event.mozMovementY || event.webkitMovementY || 0;
            this.mouseMovement.set(movementX, movementY);
        };
        this.onMouseDown = (event) => {
            if (this.isDisabled())
                return;
            this.mouseButtons.set(event.button, true);
        };
        this.onMouseUp = (event) => {
            if (this.isDisabled())
                return;
            this.mouseButtons.set(event.button, false);
        };
        this.onMouseWheel = (event) => {
            if (this.isDisabled())
                return;
            if (event.dispatcher === "html-gl")
                return;
            if (event.target !== renderer.domElement)
                return;
            this._mouseScrollDelta.x += event.deltaX;
            this._mouseScrollDelta.y += event.deltaY;
            this._mouseScrollDelta.z += event.deltaZ;
        };
        this.onResize = () => {
            const worldContainer = document.getElementById("world-container");
            if (worldContainer) {
                this.viewHalfX = worldContainer.offsetWidth / 2;
                this.viewHalfY = worldContainer.offsetHeight / 2;
            }
            else {
                this.viewHalfX = window.innerWidth / 2;
                this.viewHalfY = window.innerHeight / 2;
            }
        };
        this.resetKeys = () => {
            this.reset();
        };
        this.onDeactivate = () => {
            this.reset();
            this.gamepadsDevice.inputsActive = false;
        };
        this.handleInputMappingFacet = (uid, facet, meta) => {
            const model = modelPool.getModel(uid);
            if (model === undefined)
                return;
            const room = model.getRoom();
            if (room === undefined)
                return;
            let mappingData = this.roomInputMapping.get(room.getUID());
            if (mappingData === undefined) {
                mappingData = {
                    models: new Map()
                };
                this.roomInputMapping.set(room.getUID(), mappingData);
            }
            let inputMappingModel = mappingData.models.get(uid);
            if (inputMappingModel === undefined) {
                inputMappingModel = {
                    createdInputTime: 0,
                    createdMovementTime: 0
                };
                mappingData.models.set(uid, inputMappingModel);
            }
            inputMappingModel.createdInputTime = meta.timeStamp;
            inputMappingModel.inputMapping = facet.params;
            if (this.currentRoomModelUID !== room.getUID() || this.needsUpdate) {
                this.needsUpdate = false;
                this.updateAxisMapping();
            }
        };
        this.handleRemoveInputMappingFacet = (uid, {}, meta) => {
            const model = modelPool.getModel(uid);
            if (model === undefined)
                return;
            const room = model.getRoom();
            if (room === undefined)
                return;
            const roomData = this.roomInputMapping.get(room.getUID());
            if (!roomData)
                return;
            const inputMappingModel = roomData.models.get(uid);
            if (!inputMappingModel)
                return;
            this.removeModelFromRoom(roomData, room.getUID(), inputMappingModel, uid, meta.timeStamp);
            if (this.currentRoomModelUID !== room.getUID() || this.needsUpdate) {
                this.needsUpdate = false;
                this.updateAxisMapping();
            }
        };
        this.handleMovementInputMappingFacet = (uid, facet, meta) => {
            const model = modelPool.getModel(uid);
            if (model === undefined)
                return;
            const room = model.getRoom();
            if (room === undefined)
                return;
            let mappingData = this.roomInputMapping.get(room.getUID());
            if (!mappingData) {
                mappingData = {
                    models: new Map()
                };
                this.roomInputMapping.set(room.getUID(), mappingData);
            }
            let inputMappingModel = mappingData.models.get(uid);
            if (!inputMappingModel) {
                inputMappingModel = {
                    createdInputTime: 0,
                    createdMovementTime: 0
                };
                mappingData.models.set(uid, inputMappingModel);
            }
            inputMappingModel.createdMovementTime = meta.timeStamp;
            inputMappingModel.movementMapping = facet.params;
            if (this.currentRoomModelUID !== room.getUID() || this.needsUpdate) {
                this.needsUpdate = false;
                this.updateAxisMapping();
            }
        };
        this.handleRoomChangedMessage = (msg) => {
            if (this.currentRoomModelUID === msg.uid)
                return;
            const newRoomModel = modelPool.getModel(msg.uid);
            if (newRoomModel === undefined)
                return;
            this.currentRoomModelUID = newRoomModel.getUID();
            this.updateAxisMapping();
        };
        this.handleRemoveSubModelMessage = (msg, meta) => {
            let roomData = this.roomInputMapping.get(msg.uid);
            if (roomData) {
                for (const [uid, model] of roomData.models) {
                    this.removeModelFromRoom(roomData, msg.uid, model, uid, meta.timeStamp);
                }
                this.roomInputMapping.delete(msg.uid);
                return;
            }
            const model = modelPool.getModel(msg.uid);
            if (model === undefined)
                return;
            const room = model.getRoom();
            if (room === undefined)
                return;
            roomData = this.roomInputMapping.get(room.getUID());
            if (!roomData)
                return;
            const inputMappingModel = roomData.models.get(msg.uid);
            if (!inputMappingModel)
                return;
            this.removeModelFromRoom(roomData, room.getUID(), inputMappingModel, msg.uid, meta.timeStamp);
        };
        this.handleControllerButtonMessage = (msg) => {
            // ToDo: real input mapping
            if (msg.input !== ButtonSignal.CLICKED)
                return;
            let action = InputFlaVRDefaultActionType.Primary;
            switch (msg.buttonType) {
                case ButtonType.SELECT:
                    action = InputFlaVRDefaultActionType.Primary;
                    break;
                case ButtonType.CLICK:
                    action = InputFlaVRDefaultActionType.Secondary;
                    break;
                case ButtonType.GRAB:
                    action = InputFlaVRDefaultActionType.Tertiary;
                    break;
                case ButtonType.DISCARD:
                    action = InputFlaVRDefaultActionType.Quaternary;
                    break;
                default:
                    return;
            }
            this.connection.send(new InputFlaVRDefaultActionMessage(action, msg.uid));
        };
        this.handleRegisterInputActionMessage = (msg, askId, meta) => {
            if (this.workerActions.has(msg.name)) {
                throw new Error(`Action ${msg.name} from worker already registered`);
            }
            const actionIndex = this.nextActionIndex++;
            const workerId = meta.source.workerId;
            const action = new InputAction(msg.name, () => {
                // send direct to worker
                this.connection.send(new InputActionTriggeredMessage(actionIndex), undefined, undefined, undefined, workerId);
            }, msg.description);
            action._fromWorker(workerId, actionIndex);
            this.workerActions.set(msg.name, action);
            if (askId !== undefined) {
                this.connection.answer(askId, new ResponseRegisterInputActionMessage(actionIndex));
            }
        };
        this.handleUnregisterInputActionMessage = (msg) => {
            const action = this.workerActions.get(msg.name);
            if (!action)
                return;
            action.clear();
            this.workerActions.delete(msg.name);
        };
        this.handleInputActionKeyBindingsMessage = (msg) => {
            let action;
            for (const a of this.workerActions.values()) {
                if (!a.worker)
                    continue;
                if (a.worker.actionIndex === msg.actionIdx) {
                    action = a;
                    break;
                }
            }
            if (!action)
                return;
            for (const key in msg.keys) {
                const data = msg.keys[key];
                const instance = action.addKey(key);
                if (data.ctrl)
                    instance.addSpecial(InputKeySpecial.Ctrl);
                if (data.shift)
                    instance.addSpecial(InputKeySpecial.Shift);
                if (data.alt)
                    instance.addSpecial(InputKeySpecial.Alt);
                if (data.cmd)
                    instance.addSpecial(InputKeySpecial.Cmd);
                if (data.isDev)
                    instance.isForDev = true;
            }
        };
        this.registerDefaultKeyAction("toggleToolViewer", "AltRight", "Toggle tool belt");
        this.setDefaultAxesMapping();
        this.updateAxisMapping();
        this.onResize();
        this.connection.subscribeByFacetType(InputMappingFacet, this.handleInputMappingFacet);
        this.connection.subscribeByRemoveFacetType(InputMappingFacet, this.handleRemoveInputMappingFacet);
        this.connection.subscribeByFacetType(MovementInputMappingFacet, this.handleMovementInputMappingFacet);
        this.connection.subscribeByType(RoomChangedMessage, this.handleRoomChangedMessage);
        this.connection.subscribeByType(RemoveSubModelMessage, this.handleRemoveSubModelMessage);
        this.connection.subscribeByType(LayoutUpdatedMessage, () => {
            this.setDefaultAxesMapping();
            this.updateAxisMapping();
            this.needsUpdate = true;
        });
        this.connection.subscribeByType(ControllerButtonMessage, this.handleControllerButtonMessage);
        this.connection.subscribeByTypeOrAskRequest(RegisterInputActionMessage, this.handleRegisterInputActionMessage);
        this.connection.subscribeByType(UnregisterInputActionMessage, this.handleUnregisterInputActionMessage);
        this.connection.subscribeByType(InputActionKeyBindingsMessage, this.handleInputActionKeyBindingsMessage);
    }
    getKeyFromLayoutMap(code) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.keyboardLayoutMap) {
                return this.keyboardLayoutMap.get(code);
            }
            // This is an experimental technology
            if (!("keyboard" in navigator))
                return;
            const keyboard = navigator.keyboard;
            if (!("getLayoutMap" in keyboard))
                return;
            if (typeof keyboard.getLayoutMap() !== "function")
                return;
            this.keyboardLayoutMap = yield keyboard.getLayoutMap();
            return this.keyboardLayoutMap.get(code);
        });
    }
    registerListeners() {
        const worldContainer = document.getElementById("world-container");
        if (!worldContainer) {
            console.warn("InputService: Can't find world-container element");
            return;
        }
        document.addEventListener("keydown", this.onKeyDown, false);
        document.addEventListener("keyup", this.onKeyUp, false);
        document.addEventListener("pointermove", this.onMouseMove, false);
        worldContainer.addEventListener("pointerdown", this.onMouseDown);
        worldContainer.addEventListener("pointerup", this.onMouseUp);
        document.addEventListener("pointerup", this.onMouseUp, false);
        worldContainer.addEventListener("wheel", this.onMouseWheel, { passive: true });
        document.addEventListener("contextmenu", (event) => {
            event.preventDefault();
        });
        // renderer.domElement.addEventListener("resize", this.onResize, false);
        if (!this.canvasResizeObserver) {
            this.canvasResizeObserver = new ResizeObserver(this.onResize);
        }
        this.canvasResizeObserver.observe(renderer.domElement);
        worldContainer.addEventListener("onfocusout", this.onDeactivate, false);
        window.addEventListener("focus", () => {
            this.gamepadsDevice.inputsActive = true;
        }, false);
        worldContainer.addEventListener("contextmenu", this.resetKeys, false);
        worldContainer.addEventListener("blur", this.onDeactivate);
        window.addEventListener("blur", this.onDeactivate);
        window.addEventListener("onpagehide", this.onDeactivate);
        worldContainer.addEventListener("webkitvisibilitychange", this.resetKeys);
        worldContainer.addEventListener("visibilitychange", () => {
            if (document.hidden) {
                this.resetKeys();
                this.gamepadsDevice.inputsActive = false;
            }
        }, false);
        this.onResize();
        this.linkMovementPreferences();
    }
    linkMovementPreferences() {
        this.userMouseSensitivity = preferencesService.getPreferredValue("mouseSensitivity", 1);
        preferencesService.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "mouseSensitivity")
                return false;
            if (key === "mouseSensitivity") {
                this.userMouseSensitivity = parseFloat(newValue);
            }
            this.updateAxisMapping();
            return true;
        }));
        this.updateAxisMapping();
    }
    dispose() {
        var _a;
        const worldContainer = document.getElementById("world-container");
        if (!worldContainer) {
            console.warn("InputService: Can't find world-container element");
            return;
        }
        worldContainer.removeEventListener("keydown", this.onKeyDown, false);
        worldContainer.removeEventListener("keyup", this.onKeyUp, false);
        worldContainer.removeEventListener("pointermove", this.onMouseMove, false);
        worldContainer.removeEventListener("pointerdown", this.onMouseDown);
        worldContainer.removeEventListener("pointerup", this.onMouseUp);
        worldContainer.removeEventListener("wheel", this.onMouseWheel, false);
        // renderer.domElement.removeEventListener("resize", this.onResize, false);
        (_a = this.canvasResizeObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
        window.removeEventListener("blur", this.resetKeys, false);
    }
    get mouseScrollDelta() {
        return this._mouseScrollDelta;
    }
    isMouseButtonDown(button) {
        if (typeof button === "string") {
            switch (button) {
                case "MouseLeft":
                    return this.mouseButtons.get(0) || false;
                case "MouseRight":
                    return this.mouseButtons.get(1) || false;
                case "MouseMiddle":
                    return this.mouseButtons.get(2) || false;
            }
            return false;
        }
        return this.mouseButtons.get(button) || false;
    }
    isKeyDownByCode(code) {
        const data = this.codesDown.get(code);
        if (!data)
            return false;
        return data.state !== KeyState.Released;
    }
    isKeyDown(key) {
        const data = this.keysDown.get(key);
        if (!data)
            return false;
        return data.state !== KeyState.Released;
    }
    emulateKeyPress(keyName, modifiers = {}) {
        var _a, _b, _c, _d, _e, _f;
        const keyDetails = inputKeyMappingService.getKeyDetails(keyName);
        if (keyDetails === undefined || keyDetails.deviceType.type !== InputKeyDetailsDeviceEnum.Keyboard) {
            console.warn(`[inputs] emulateKeyPress: key ${keyName} not found or not a keyboard key`);
            return false;
        }
        const code = keyDetails.deviceType.code;
        const key = (_b = (_a = keyDetails.deviceType.key) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : keyName.toLowerCase();
        const previousSpecialKeysState = Object.assign({}, this.specialKeysState);
        this.specialKeysState.shift = (_c = modifiers.shift) !== null && _c !== void 0 ? _c : false;
        this.specialKeysState.ctrl = (_d = modifiers.ctrl) !== null && _d !== void 0 ? _d : false;
        this.specialKeysState.alt = (_e = modifiers.alt) !== null && _e !== void 0 ? _e : false;
        this.specialKeysState.cmd = (_f = modifiers.cmd) !== null && _f !== void 0 ? _f : false;
        this.triggerKeyAction(ButtonSignal.PRESSED, code, key);
        this.triggerKeyAction(ButtonSignal.CLICKED, code, key);
        this.triggerKeyAction(ButtonSignal.RELEASED, code, key);
        const keys = (code !== undefined ? KEY_MAP.get(code) : undefined) || KEY_MAP.get(key);
        if (keys !== undefined) {
            for (const data of keys) {
                if (!data.canTrigger(this.specialKeysState))
                    continue;
                data.action.callback();
            }
        }
        this.specialKeysState.shift = previousSpecialKeysState.shift;
        this.specialKeysState.ctrl = previousSpecialKeysState.ctrl;
        this.specialKeysState.alt = previousSpecialKeysState.alt;
        this.specialKeysState.cmd = previousSpecialKeysState.cmd;
        return true;
    }
    setEnabled(id) {
        this.disabled.delete(id);
    }
    setDisabled() {
        while (this.disabled.has(this.lastDisabledId++)) { }
        this.disabled.add(this.lastDisabledId);
        this.reset();
        return this.lastDisabledId;
    }
    isDisabled() {
        return this.disabled.size !== 0;
    }
    registerAction(name, callback, description) {
        const existingAction = this.actions.get(name);
        if (existingAction != undefined) {
            throw new Error(`action "${name}" already registered`);
        }
        const action = new InputAction(name, callback, description);
        this.actions.set(name, action);
        this.onActionsChangedDelegate.invoke();
        return action;
    }
    deleteAction(name) {
        const action = this.actions.get(name);
        if (action === undefined)
            return;
        action.clear();
        this.actions.delete(name);
        this.onActionsChangedDelegate.invoke();
    }
    findAction(name) {
        return this.actions.get(name);
    }
    findActionForKey(key) {
        return KEY_MAP.get(key);
    }
    *getActionIterator() {
        for (const action of this.actions.values()) {
            yield action;
        }
        for (const action of this.workerActions.values()) {
            yield action;
        }
    }
    hasActionDevKey(actionName) {
        const action = this.actions.get(actionName);
        if (action) {
            return action.hasDevKey();
        }
        const workerActions = this.workerActions.get(actionName);
        if (workerActions) {
            return workerActions.hasDevKey();
        }
        return false;
    }
    *getCurrentKeyActionBindingIterator() {
        const actions = new Map();
        for (const bindings of this.defaultKeyActionsBinding.values()) {
            for (const [actionName, binding] of bindings) {
                let actionData = actions.get(actionName);
                if (!actionData) {
                    const actionInfo = this.currentActionInfos.get(actionName);
                    actionData = {
                        keys: [],
                        descriptive: actionInfo ? actionInfo.descriptive : undefined,
                        hasDevKey: this.hasActionDevKey(actionName)
                    };
                    actions.set(actionName, actionData);
                }
                actionData.keys.push(binding.key);
            }
        }
        for (const bindings of this.currentKeyActionsBinding.values()) {
            for (const [actionName, binding] of bindings) {
                let actionData = actions.get(actionName);
                if (!actionData) {
                    const actionInfo = this.currentActionInfos.get(actionName);
                    actionData = {
                        keys: [],
                        descriptive: actionInfo ? actionInfo.descriptive : undefined,
                        hasDevKey: this.hasActionDevKey(actionName)
                    };
                    actions.set(actionName, actionData);
                }
                actionData.keys.push(binding.key);
            }
        }
        for (const [actionName, actionData] of actions) {
            yield {
                actionName,
                keys: actionData.keys,
                description: actionData.descriptive,
                hasDevKey: actionData.hasDevKey
            };
        }
    }
    *getCurrentAxisBindingIterator() {
        for (const [name, axisData] of this.currentAxisMapping) {
            yield {
                name,
                axisData
            };
        }
    }
    getAxisValue(name) {
        if (this.isDisabled()) {
            return 0;
        }
        const axis = this.currentAxisMapping.get(name);
        if (!axis) {
            console.warn(`getAxisValue: mapping ${name} not found`);
            return 0;
        }
        return this.getAxisValueByMapping(axis);
    }
    getAxisValueByMapping(axis) {
        let result = 0;
        for (const [mapping, scale] of axis.mappings) {
            const keyDetails = inputKeyMappingService.getKeyDetails(mapping);
            if (!keyDetails)
                continue;
            const config = this.currentAxisConfigs.get(mapping);
            let value = keyDetails.getAxisValue(config);
            if (config) {
                value *= config.sensitivity;
                if (config.invert) {
                    value *= -1;
                }
            }
            result += value * scale;
        }
        return result;
    }
    update() {
        this.gamepadsDevice.poll();
        if (this.isDisabled())
            return;
        this.updateKeyState();
        this.updateAxesState();
        if (!this.pendingMessages.isEmpty()) {
            this.connection.sendMultiple(this.pendingMessages.get());
            this.pendingMessages.clear();
        }
    }
    onAfterInput() {
        this._mouseScrollDelta.setScalar(0);
        this.mouseMovement.setScalar(0);
    }
    registerDefaultKeyAction(actionName, keyName, description) {
        let foundActionIdx;
        for (const [actionIdx, aName] of DEFAULT_KEY_ACTION_MAPPING) {
            if (aName !== actionName)
                continue;
            foundActionIdx = actionIdx;
        }
        if (foundActionIdx === undefined) {
            throw new Error(`Can't find default action ${actionName}`);
        }
        const keyDetails = inputKeyMappingService.getKeyDetails(keyName);
        if (!keyDetails) {
            throw new Error(`Can't find key details for key name ${keyName}`);
        }
        this.defaultKeyActionInfos.set(actionName, {
            descriptive: description
        });
        let bindings = this.defaultKeyActionsBinding.get(keyName);
        if (!bindings) {
            bindings = new Map();
            this.defaultKeyActionsBinding.set(keyName, bindings);
        }
        bindings.set(actionName, {
            actionIdx: foundActionIdx,
            key: {
                key: keyDetails.indexMapping,
                state: ButtonSignal.RELEASED,
                shift: false,
                ctrl: false,
                alt: false,
                cmd: false
            }
        });
    }
    setKeyDown(map, keyOrCode, time) {
        let data = map.get(keyOrCode);
        if (!data) {
            map.set(keyOrCode, (data = {
                state: KeyState.Pressed,
                lastChanged: time,
                doubleClick: false
            }));
        }
        else {
            data.doubleClick = time - data.lastChanged <= DOUBLE_CLICK_TIME;
        }
        if (data.state !== KeyState.Pressed) {
            data.lastChanged = time;
            data.state = KeyState.Pressed;
        }
    }
    onPreKeyUp(map, keyOrCode, time) {
        let data = map.get(keyOrCode);
        if (!data) {
            map.set(keyOrCode, (data = {
                state: KeyState.Released,
                lastChanged: time,
                doubleClick: false
            }));
        }
        return data;
    }
    updateKeyState() {
        const time = Date.now();
        for (const [code, data] of this.codesDown) {
            if (data.state !== KeyState.Pressed)
                continue;
            if (time - data.lastChanged <= HOLD_TIME)
                continue;
            data.state = KeyState.Hold;
            this.triggerKeyAction(ButtonSignal.HOLD, code);
        }
        for (const [key, data] of this.keysDown) {
            if (data.state !== KeyState.Pressed)
                continue;
            if (time - data.lastChanged <= HOLD_TIME)
                continue;
            data.state = KeyState.Hold;
            this.triggerKeyAction(ButtonSignal.HOLD, undefined, key);
        }
    }
    updateAxesState() {
        for (const axisData of this.currentAxisMapping.values()) {
            const value = this.getAxisValueByMapping(axisData);
            const changed = axisData.prevValue !== value;
            if (!changed)
                continue;
            axisData.prevValue = value;
            // ToDo: trigger axis action
            const axisIndex = 0;
            this.pendingMessages.push(new InputAxisValueMessage(value, axisIndex));
        }
    }
    reset() {
        for (const [code, key] of this.codesDown) {
            if (key.state === KeyState.Released)
                continue;
            this.onKeyUp({
                code
            });
        }
        for (const [key, data] of this.keysDown) {
            if (data.state === KeyState.Released)
                continue;
            this.onKeyUp({
                key
            });
        }
        this.codesDown.clear();
        this.keysDown.clear();
        this.mouseButtons.clear();
        this._mouseScrollDelta.setScalar(0);
        this.mouseMovement.setScalar(0);
        this.specialKeysState.shift = false;
        this.specialKeysState.ctrl = false;
        this.specialKeysState.alt = false;
        this.specialKeysState.cmd = false;
    }
    removeModelFromRoom(roomData, roomUid, model, modelUid, timestamp) {
        if (model.createdInputTime <= timestamp) {
            model.inputMapping = undefined;
        }
        if (model.createdMovementTime <= timestamp) {
            model.movementMapping = undefined;
        }
        if (!model.inputMapping && !model.movementMapping) {
            roomData.models.delete(modelUid);
            if (roomData.models.size === 0) {
                this.roomInputMapping.delete(roomUid);
            }
        }
    }
    updateAxisMapping() {
        this.currentAxisMapping.clear();
        this.currentActionInfos.clear();
        this.currentKeyActionsBinding.clear();
        for (const [actionName, info] of this.defaultKeyActionInfos) {
            this.currentActionInfos.set(actionName, {
                descriptive: info.descriptive
            });
        }
        for (const [name, data] of this.defaultAxisMapping) {
            this.currentAxisMapping.set(name, {
                descriptivePositiveName: data.descriptivePositiveName,
                descriptiveNegativeName: data.descriptiveNegativeName,
                mappings: new Map(data.keys),
                prevValue: 0
            });
        }
        const result = this.findNewestInputMapping(this.currentRoomModelUID);
        if (!result)
            return;
        for (let idx = 0; idx < result.inputMapping.actions.length; idx++) {
            const action = result.inputMapping.actions[idx];
            this.currentActionInfos.set(action.name, {
                descriptive: action.descriptive
            });
            for (const key of action.keys) {
                const keyDetails = inputKeyMappingService.getKeyDetailsByIndex(key.key);
                if (!keyDetails) {
                    continue;
                }
                let binding = this.currentKeyActionsBinding.get(keyDetails.keyName);
                if (!binding) {
                    binding = new Map();
                    this.currentKeyActionsBinding.set(keyDetails.keyName, binding);
                }
                binding.set(action.name, {
                    uid: result.uid,
                    actionIdx: idx,
                    key
                });
            }
        }
        for (const axes of result.inputMapping.axes) {
            let axisData = this.currentAxisMapping.get(axes.name);
            if (!axisData) {
                axisData = {
                    mappings: new Map(),
                    prevValue: 0
                };
                this.currentAxisMapping.set(axes.name, axisData);
            }
            axisData.mappings.clear();
            axisData.descriptivePositiveName = axes.descriptivePositiveName;
            axisData.descriptiveNegativeName = axes.descriptiveNegativeName;
            for (const keyData of axes.keys) {
                const keyDetails = inputKeyMappingService.getKeyDetailsByIndex(keyData.key);
                if (!keyDetails)
                    continue;
                axisData.mappings.set(keyDetails.keyName, keyData.scale);
            }
        }
        this.currentAxisConfigs.clear();
        this.currentAxisConfigs.set("MouseX", {
            keyIdx: 0,
            deadZone: 0.0,
            sensitivity: this.userMouseSensitivity,
            invert: false
        });
        this.currentAxisConfigs.set("MouseY", {
            keyIdx: 0,
            deadZone: 0.0,
            sensitivity: this.userMouseSensitivity,
            invert: false
        });
        this.currentAxisConfigs.set("MouseWheel", {
            keyIdx: 0,
            deadZone: 0.0,
            sensitivity: this.mouseWheelSensitivity,
            invert: false
        });
        if (result.inputMapping.axisConfigs && result.inputMapping.axisConfigs.length > 0) {
            for (const config of result.inputMapping.axisConfigs) {
                const keyDetails = inputKeyMappingService.getKeyDetailsByIndex(config.keyIdx);
                if (!keyDetails)
                    continue;
                this.currentAxisConfigs.set(keyDetails.keyName, config);
            }
        }
    }
    findNewestInputMapping(roomUid) {
        const roomMapping = this.roomInputMapping.get(roomUid);
        if (!roomMapping)
            return undefined;
        let lastTime = 0;
        let inputMapping;
        let foundUid;
        for (const [uid, m] of roomMapping.models) {
            if (m.inputMapping && m.createdInputTime >= lastTime) {
                lastTime = m.createdInputTime;
                inputMapping = m.inputMapping;
                foundUid = uid;
            }
        }
        if (!inputMapping) {
            return undefined;
        }
        if (!foundUid) {
            return undefined;
        }
        return {
            uid: foundUid,
            inputMapping
        };
    }
    triggerKeyAction(state, code, key) {
        const defaultBindings = (code && this.defaultKeyActionsBinding.get(code)) || (key && this.defaultKeyActionsBinding.get(key));
        if (defaultBindings) {
            for (const data of defaultBindings.values()) {
                if (data.key.state !== state)
                    continue;
                this.pendingMessages.push(new InputActionMessage(data.actionIdx));
            }
        }
        const bindings = (code && this.currentKeyActionsBinding.get(code)) || (key && this.currentKeyActionsBinding.get(key));
        if (!bindings)
            return;
        for (const data of bindings.values()) {
            if (data.key.state !== state)
                continue;
            this.pendingMessages.push(new InputActionMessage(data.actionIdx, data.uid));
        }
    }
    setDefaultAxesMapping() {
        const moveForward = new Map();
        moveForward.set("KeyW", 1);
        moveForward.set("KeyS", -1);
        moveForward.set("ArrowUp", 1);
        moveForward.set("ArrowDown", -1);
        moveForward.set("GamepadLeftY", 1);
        this.defaultAxisMapping.set("moveForward", {
            descriptivePositiveName: "Move forward",
            descriptiveNegativeName: "Move backward",
            keys: moveForward
        });
        const moveRight = new Map();
        moveRight.set("KeyA", -1);
        moveRight.set("KeyD", 1);
        moveRight.set("GamepadLeftX", 1);
        this.defaultAxisMapping.set("moveRight", {
            descriptivePositiveName: "Move right",
            descriptiveNegativeName: "Move left",
            keys: moveRight
        });
        const moveUp = new Map();
        moveUp.set("KeyR", 1);
        moveUp.set("KeyF", -1);
        moveUp.set("GamepadRightShoulder", 1);
        moveUp.set("GamepadRightTrigger", -1);
        this.defaultAxisMapping.set("moveUp", {
            descriptivePositiveName: "Move upwards",
            descriptiveNegativeName: "Move downwards",
            keys: moveUp
        });
        const turn = new Map();
        turn.set("ArrowLeft", 1);
        turn.set("ArrowRight", -1);
        turn.set("MouseX", 1);
        turn.set("GamepadRightX", -1);
        this.defaultAxisMapping.set("turn", {
            descriptivePositiveName: "Turn right",
            descriptiveNegativeName: "Turn left",
            keys: turn
        });
        const lookUp = new Map();
        lookUp.set("BracketRight", 1);
        lookUp.set("Backslash", -1);
        lookUp.set("MouseY", 1);
        lookUp.set("GamepadRightY", -1);
        this.defaultAxisMapping.set("lookUp", {
            descriptivePositiveName: "Look up",
            descriptiveNegativeName: "Look down",
            keys: lookUp
        });
    }
}
export const inputService = new InputService();
